/**
 * Before your first visit — one-screen warm welcome shown ONCE after a new
 * patient completes registration from a Book flow (Register.jsx with
 * paymentContext === "booking"). Content adapts to the picked service:
 *   • Standard assessment   — talk through symptoms, history, movement/strength assessment
 *   • Performance / S&C     — training history, testing battery, no medical scans emphasis
 *   • Post-operative        — surgical paperwork, consultant protocol
 *
 * After the first render we drop a localStorage flag so the page never blocks
 * a returning user again — subsequent bookings go straight to /app/appointments.
 */
import { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, HeartPulse, Timer, Sparkles, ClipboardList, ShieldCheck, Info } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";

const SEEN_KEY = "fl_seen_before_first_visit_v1";

/**
 * Very small heuristic — look at the service name in the picked redirect URL
 * and pick the most sensible tone. We're intentionally conservative: anything
 * we can't classify falls back to the standard first-assessment content.
 */
function detectContext(service, contextParam) {
  if (contextParam && ["assessment", "performance", "postop"].includes(contextParam)) return contextParam;
  const s = (service || "").toLowerCase();
  if (!s) return "assessment";
  if (/post[-\s]?op|postoperative|post\s?operative|surgical|acl reconstruction|surgery/.test(s)) return "postop";
  if (/performance|s&c|strength|conditioning|athletic|return.to.sport|force plate|testing|screening.*sport/.test(s)) return "performance";
  return "assessment";
}

const COPY = {
  assessment: {
    eyebrow: "Before your first visit",
    title: "You're all set 👋",
    intro: "Your first Functional LAB appointment is designed to understand where you are now, what's limiting you, and what you want to get back to.",
    doListTitle: "At your first visit we'll usually:",
    doList: [
      "Talk through your symptoms, history and goals",
      "Assess movement, strength and relevant physical measures",
      "Explain what we find in plain English",
      "Agree your next steps and initial plan",
    ],
    bringListTitle: "What to bring",
    bringList: [
      "Comfortable clothing you can move in",
      "Any relevant reports, scans or referral letters",
      "Your current medication or injury information if relevant",
      "Trainers if your assessment involves movement or exercise",
    ],
    reassure: "No need to prepare anything else — we'll guide you through it.",
  },
  performance: {
    eyebrow: "Before your first visit",
    title: "You're all set 👋",
    intro: "Your first Functional LAB session is built around your sport, your goals and the numbers — a benchmark you can beat next time.",
    doListTitle: "At your first visit we'll usually:",
    doList: [
      "Talk through your training history and current schedule",
      "Test movement, strength and (where relevant) power / force output",
      "Map your goals to a periodised plan",
      "Agree the frequency and format that fits your season",
    ],
    bringListTitle: "What to bring",
    bringList: [
      "Kit you'd normally train in — trainers or gym shoes",
      "A recent training log or programme if you already have one",
      "Any prior testing data (VO₂, force plate, wearable exports) — optional",
      "A water bottle — you'll be moving",
    ],
    reassure: "No need to peak or taper — come as you are, we'll test what's real.",
  },
  postop: {
    eyebrow: "Before your first visit",
    title: "You're all set 👋",
    intro: "Your first Functional LAB appointment after surgery is about understanding what's already been done, where you are in healing, and building the safest possible next step.",
    doListTitle: "At your first visit we'll usually:",
    doList: [
      "Talk through your surgery, timelines and current symptoms",
      "Review your consultant's protocol and any current restrictions",
      "Gently assess movement and load tolerance",
      "Agree a phased rehabilitation plan with clear milestones",
    ],
    bringListTitle: "What to bring",
    bringList: [
      "Your surgical report and any consultant / physio letters",
      "The rehabilitation protocol from your surgical team if you have it",
      "Any current medication list",
      "Comfortable clothing you can move in (and trainers if you're weight-bearing)",
    ],
    reassure: "If you're unsure about any restriction — bring the question. We'll work within it.",
  },
};

export default function BeforeFirstVisit() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/app/appointments";
  const service = params.get("service") || "";
  const contextParam = params.get("context") || "";
  const ctx = detectContext(service, contextParam);
  const c = COPY[ctx];

  // Persist "seen" the moment this screen renders so a page reload / back
  // button won't loop the user through the intro twice.
  useEffect(() => {
    try { localStorage.setItem(SEEN_KEY, new Date().toISOString()); } catch {}
  }, []);

  const continueHref = redirect.startsWith("/") ? redirect : "/app/appointments";

  const iconFor = (i) => [ClipboardList, HeartPulse, Sparkles, Timer][i % 4];

  return (
    <div className="min-h-screen fl-noise fl-page flex items-center justify-center p-6" style={{ background: "var(--fl-bg)" }}>
      <div className="absolute top-6 right-6 flex items-center gap-2"><LanguageSwitcher /><ThemeToggle /></div>
      <div className="w-full max-w-2xl">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="fl-card fl-card-elevated p-8 md:p-10 relative overflow-hidden" data-testid="before-first-visit-card">
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(255,69,0,0.10), transparent 60%)" }} />
          <div className="relative">
            <div className="fl-label text-[color:var(--fl-orange)] mb-3" data-testid="bfv-eyebrow">{c.eyebrow}</div>
            <h1 className="fl-heading text-3xl md:text-4xl" data-testid="bfv-title">{c.title}</h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed" style={{ color: "var(--fl-muted)" }} data-testid="bfv-intro">
              {c.intro}
            </p>

            <div className="mt-8">
              <div className="fl-label mb-3">{c.doListTitle}</div>
              <ul className="space-y-2" data-testid="bfv-do-list">
                {c.doList.map((item, i) => {
                  const Icon = iconFor(i);
                  return (
                    <li key={i} className="flex items-start gap-3 text-sm md:text-[15px]">
                      <span className="mt-0.5 shrink-0 rounded-full p-1.5" style={{ background: "color-mix(in oklab, var(--fl-orange) 14%, transparent)" }}>
                        <Icon size={13} className="text-[color:var(--fl-orange)]" />
                      </span>
                      <span>{item}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="mt-8">
              <div className="fl-label mb-3 flex items-center gap-2">
                <ShieldCheck size={13} className="text-[color:var(--fl-orange)]" />
                {c.bringListTitle}
              </div>
              <ul className="space-y-2" data-testid="bfv-bring-list">
                {c.bringList.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm md:text-[15px]">
                    <span className="mt-1 shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: "var(--fl-orange)" }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 flex items-start gap-2 text-[12px] md:text-[13px] italic" style={{ color: "var(--fl-muted)" }} data-testid="bfv-reassure">
              <Info size={12} className="text-[color:var(--fl-orange)] mt-0.5 shrink-0" />
              <span>{c.reassure}</span>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to={continueHref}
                className="fl-btn text-sm md:text-base justify-center flex-1"
                data-testid="bfv-continue"
              >
                Continue to my booking <ArrowRight size={16} />
              </Link>
              <button
                type="button"
                onClick={() => nav("/app")}
                className="fl-btn-ghost text-xs md:text-sm justify-center"
                data-testid="bfv-skip"
              >
                Skip to dashboard
              </button>
            </div>
            <div className="mt-4 text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
              You won't see this screen again — it's here to make the first visit feel less like a cold start.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
